import { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { lazyNamed } from './lib/lazyNamed';

const Home = lazyNamed(() => import('./pages/Home'), 'Home');
const SignIn = lazyNamed(() => import('./pages/SignIn'), 'SignIn');
const SignUp = lazyNamed(() => import('./pages/SignUp'), 'SignUp');
const Profile = lazyNamed(() => import('./pages/Profile'), 'Profile');
const ChangePassword = lazyNamed(
  () => import('./pages/ChangePassword'),
  'ChangePassword',
);
const ResetPassword = lazyNamed(
  () => import('./pages/ResetPassword'),
  'ResetPassword',
);

const GettingStarted = lazyNamed(
  () => import('./pages/GettingStarted'),
  'GettingStarted',
);
const ApiList = lazyNamed(() => import('./pages/apis/ApiList'), 'ApiList');
const ApiDetail = lazyNamed(
  () => import('./pages/apis/ApiDetail'),
  'ApiDetail',
);

const CredentialRequestList = lazyNamed(
  () => import('./pages/credentials/CredentialRequestList'),
  'CredentialRequestList',
);
const CredentialRequestDetail = lazyNamed(
  () => import('./pages/credentials/CredentialRequestDetail'),
  'CredentialRequestDetail',
);
const CredentialRequestReview = lazyNamed(
  () => import('./pages/credentials/CredentialRequestReview'),
  'CredentialRequestReview',
);

const ActivityDashboard = lazyNamed(
  () => import('./pages/activity/ActivityDashboard'),
  'ActivityDashboard',
);

const Dashboard = lazyNamed(
  () => import('./pages/analytics/Dashboard'),
  'Dashboard',
);
const CredentialReviewList = lazyNamed(
  () => import('./pages/ops/CredentialReviewList'),
  'CredentialReviewList',
);
const InternalClientList = lazyNamed(
  () => import('./pages/ops/InternalClientList'),
  'InternalClientList',
);

const ErrorReference = lazyNamed(
  () => import('./pages/errors/ErrorReference'),
  'ErrorReference',
);
const ErrorDetail = lazyNamed(
  () => import('./pages/errors/ErrorDetail'),
  'ErrorDetail',
);

const NotFound = lazyNamed(
  () => import('./pages/errors/NotFound'),
  'NotFound',
);
const ServerError = lazyNamed(
  () => import('./pages/errors/ServerError'),
  'ServerError',
);
const AccessDenied = lazyNamed(
  () => import('./pages/errors/AccessDenied'),
  'AccessDenied',
);

export function App() {
  // Two-tier error-boundary topology:
  //   * Outer boundary wraps `AppShell` so a render error in shell-level chrome
  //     (Navigation, Footer, providers wrapping children) still surfaces a
  //     fallback page rather than a blank screen.
  //   * Inner boundary wraps the `<Routes>` tree so a render error inside a
  //     leaf route preserves shell chrome (nav + footer) and lets the user
  //     navigate away from the broken page without a full reload.
  return (
    <ErrorBoundary>
      <AppShell>
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner label="Loading page..." />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signin-sso" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/signup-oauth" element={<SignUp />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* API routes (public) */}
              <Route path="/getting-started" element={<GettingStarted />} />
              <Route path="/apis" element={<ApiList />} />
              <Route path="/apis/:apiName" element={<ApiDetail />} />
              <Route path="/apis/:apiName/:version/*" element={<ApiDetail />} />

              {/* Error reference (public).
                  Serves the RFC 9457 `type` URIs returned in API error bodies,
                  e.g. `/errors/validation-error`. */}
              <Route path="/errors" element={<ErrorReference />} />
              <Route path="/errors/:slug" element={<ErrorDetail />} />

              {/* Protected routes (authenticated users) */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/credentials"
                element={
                  <ProtectedRoute>
                    <CredentialRequestList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/credentials/:id"
                element={
                  <ProtectedRoute>
                    <CredentialRequestDetail />
                  </ProtectedRoute>
                }
              />
              {/* Route stays mounted regardless of the server-side activityMetrics
                  access grant so direct navigation/bookmarks never 404; the page
                  itself renders a no-access notice for ungranted users. */}
              <Route
                path="/activity"
                element={
                  <ProtectedRoute>
                    <ActivityDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Ops routes (ops role required) */}
              <Route
                path="/ops/dashboard"
                element={
                  <ProtectedRoute requiredRole="ops">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ops/credential-reviews"
                element={
                  <ProtectedRoute requiredRole="ops">
                    <CredentialReviewList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ops/credential-reviews/:id"
                element={
                  <ProtectedRoute requiredRole="ops">
                    <CredentialRequestReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ops/internal-clients"
                element={
                  <ProtectedRoute requiredRole="ops">
                    <InternalClientList />
                  </ProtectedRoute>
                }
              />

              {/* Error routes.
                  `/500` is reached via the SWA platform-level `responseOverrides.500`
                  rewrite in `public/staticwebapp.config.json`, which serves
                  `/index.html` with status 500 when an upstream/origin failure occurs.
                  The SPA then matches the `*` fallback below and routes the user to
                  this `/500` element via direct navigation, or — for client-side 5xx
                  surfacing — application code can call `navigate('/500')` directly. */}
              <Route path="/500" element={<ServerError />} />
              <Route path="/access-denied" element={<AccessDenied />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AppShell>
    </ErrorBoundary>
  );
}
